import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { domains } from 'global';
import visa from 'assets/visa.svg';
import amex from 'assets/amex.svg';
import mastercard from 'assets/mastercard.svg';
import unionPay from 'assets/unionpay.svg';
import type * as statuses from 'services/store/states';
import { getUserInfos } from 'services/store/features/User/actions';
import styles from './InvoicingForm.module.css';
import ErrorAnimation from 'components/library/ErrorAnimation/ErrorAnimation';
import Button from 'components/library/Button/Button';
import SocietyForm from './SocietyForm';
import NotOwnerModal from 'components/NotOwnerModal/NotOwnerModal';
import WhatNext from './WhatNext/WhatNext';
import Transfer from 'components/Transfer';
import type { AppDispatch } from 'services/store/types';
import type { UserState } from 'services/store/features/User/types';
import type { ConfigState } from 'services/store/features/Config/types';
import type { IbanTransfer } from 'types/Iban';

const PAYMENT_METHODS = {
  CARD: 'card',
  CUSTOMER_BALANCE: 'customer_balance',
}

interface Props {
  user: UserState;
  config: ConfigState;
  transfer?: IbanTransfer,
  status: typeof statuses[keyof typeof statuses];
  actions: {
    getPaymentRedirectionLink: (paymentMethod: string) => void;
    setUserInfos: () => void;
  };
};

export default function InvoicingForm({
  user, config, transfer, status, actions,
}: Props) {
  const { t } = useTranslation();
  const dispatch: AppDispatch = useDispatch();
  const [isCompleteSociety, setIsCompleteSociety] = useState(false);
  const [isCompleteUser, setIsCompleteUser] = useState(false);
  const [selectedPaymentOption, setSelectedPayementOption] = useState(PAYMENT_METHODS.CARD);

  const [statusPayment, setStatusPayment] = useState(
    (new URLSearchParams(window.location.search).get('status')?.toLowerCase() === 'cancel') ? 'ERROR' : 'IDLE',
  );

  useEffect(() => {
    if (status === 'IDLE') {
      dispatch(getUserInfos());
    }
  }, [dispatch, status, statusPayment]);

  const handleSubmit = () => {
    if (isCompleteSociety && isCompleteUser) {
      actions.getPaymentRedirectionLink(selectedPaymentOption);
    }
  };

  const handleChange = (e: React.MouseEvent<HTMLElement>) => {
    const getName = (element: EventTarget | null): string => {
      if (element instanceof HTMLElement && 'name' in element) {
        return element.name as string;
      }
      return '';
    };
    const name = getName(e.target);
    const currentElement: HTMLElement = e.target as HTMLElement;
    const payementOption = (name) ? name : (
      getName(currentElement.parentElement)
      || getName(currentElement.parentElement && currentElement.parentElement.parentElement));
    setSelectedPayementOption(payementOption);
  };

  return (
    <>
      <section className={styles.content}>
        <div className={styles.grid}>
          <div className={styles.boxForm}>
            <div className={styles.flexBox}>
              <SocietyForm user={user} actions={actions} setIsCompleteSociety={setIsCompleteSociety} setIsCompleteUser={setIsCompleteUser} />
              <div className={styles.paymentContainer}>
                <h2 className={styles.subtitle}>
                  {t('payment.paymentMethod')}
                </h2>
                <button
                  type="button"
                  name="card"
                  className={`${styles.button} ${(selectedPaymentOption === PAYMENT_METHODS.CARD) ? styles.active : ''}`}
                  onClick={handleChange}
                >
                  <div>
                    <input
                      type="radio"
                      checked={selectedPaymentOption === 'card'}
                      readOnly
                    />
                    <span className={styles.method}>{t('payment.card')}</span>
                  </div>
                  <div className={styles.card}>
                    <img
                      src={visa}
                      alt="visa"
                    />
                    <img
                      src={amex}
                      alt="american express"
                    />
                    <img
                      src={mastercard}
                      alt="mastercard"
                    />
                    <img
                      src={unionPay}
                      alt="union pay"
                    />
                  </div>
                </button>
                <button
                  type="button"
                  className={`${styles.button} ${(selectedPaymentOption === PAYMENT_METHODS.CUSTOMER_BALANCE) ? styles.active : ''}`}
                  name="customer_balance"
                  onClick={handleChange}
                >
                  <div>
                    <input
                      type="radio"
                      checked={selectedPaymentOption === PAYMENT_METHODS.CUSTOMER_BALANCE}
                      readOnly
                    />
                    <span className={styles.method}>{t('payment.transfert.title')}</span>
                  </div>
                </button>
                {
                  transfer && (selectedPaymentOption === PAYMENT_METHODS.CUSTOMER_BALANCE) ? (
                    <Transfer transfer={transfer} />
                  ) : (
                    <div className={styles.footerContainer}>
                      <Button
                        className={styles.buttonBackTo}
                        variant="ghost"
                        type="secondary"
                        label={t('invoicingForm.backTo')}
                        onButtonClick={() => document.location.assign(domains.website)}
                      />
                      <Button
                        className={styles.submitButton}
                        variant="fill"
                        type="secondary"
                        label={t('invoicingForm.submit')}
                        disabled={!isCompleteSociety || !isCompleteUser}
                        onButtonClick={handleSubmit}
                      />
                    </div>
                  )
                }

              </div>
            </div>
          </div>
          <div className={styles.recap}>
            <p className={`${styles.bold} ${styles.title}`}>{t('invoicingForm.recap')}</p>
            <div className={styles.recapSection}>
              <p>{t('invoicingForm.administrationFees')}</p>
              <p>{`${Number.parseFloat(config.amountRegistration).toLocaleString(undefined, { minimumFractionDigits: 2 })} €`}</p>
            </div>
            <hr />
            <div className={styles.recapSection}>
              <p>{t('invoicingForm.noVAT')}</p>
              <p>{`${Number.parseFloat(config.amountRegistration).toLocaleString(undefined, { minimumFractionDigits: 2 })} €`}</p>
            </div>
            <div className={styles.recapSection}>
              <p>{t('invoicingForm.taxes')}</p>
              <p>{`${(Number.parseFloat(config.amountRegistration) * (user.vatRate / 100)).toLocaleString(undefined, { minimumFractionDigits: 2 })} €`}</p>
            </div>
            <hr />
            <div className={styles.recapSection}>
              <p className={styles.bold}>{t('invoicingForm.net')}</p>
              <p>{`${(Number.parseFloat(config.amountRegistration) + Number.parseFloat(config.amountRegistration) * (user.vatRate / 100)).toLocaleString(undefined, { minimumFractionDigits: 2 })} €`}</p>
            </div>
          </div>
          <WhatNext />
        </div>
      </section>
      {
        (statusPayment === 'ERROR')
        && (
          <div className={styles.background}>
            <section className={styles.modal}>
              <ErrorAnimation />
              <h1>
                {t('errorPayment.paymentTitle')}
              </h1>
              <p>
                {t('errorPayment.paymentDescription')}
              </p>
              <Button
                label={t('errorPayment.close')}
                onButtonClick={() => setStatusPayment('IDLE')}
              />
            </section>
          </div>
        )
      }
      {(user.isOwner === false) && (<NotOwnerModal />)}
    </>
  );
}
